import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import Dashboard from '../../layout/Dashboard'
import { Content, Heading } from '../../styles/OverviewStyle'
import { ListHead, ListRow, NavContainer, NavWrapper } from '../../styles/CrypoAssetStyle'
import { useNavigate } from 'react-router'
import { activityLogService } from '../../services/ActivityLogService'
import type { ActivityLog } from '../../../types/ApiTypes'
import Pagination from '../../components/Pagination'
import { formatDate } from '../../utils/dateHelpers'

const AdminRoleStyle: Record<string, string> = {
   'superadmin': 'bg-[#FFF0F2] text-[#C02236]',
   'admin': 'bg-[#F5F9FF] text-[#1B52B8]',
   'analyst': 'bg-[#F5FFF5] text-[#1EB81B]',
}

const RoleLabels: Record<string, string> = {
   'superadmin': 'Super Admin',
   'admin': 'Admin',
   'analyst': 'Analyst',
}

export default function ActivityLog(): React.JSX.Element {
   const [query, setQuery] = useState('')
   const [debouncedQuery, setDebouncedQuery] = useState('')
   const [logs, setLogs] = useState<ActivityLog[]>([])
   const [loading, setLoading] = useState(true)
   const [currentPage, setCurrentPage] = useState(1)
   const [totalPages, setTotalPages] = useState(0)
   const pageSize = 20
   const navigate = useNavigate()

   useEffect(() => {
      const timer = setTimeout(() => setDebouncedQuery(query), 500)
      return () => clearTimeout(timer)
   }, [query])

   useEffect(() => {
      const loadLogs = async () => {
         setLoading(true)
         try {
            const params: { pageNumber: number; pageSize: number; search?: string } = { pageNumber: currentPage, pageSize }
            if (debouncedQuery) params.search = debouncedQuery

            const response = await activityLogService.getActivityLogs(params)
            if (response.status === 'success' && response.data) {
               setLogs(response.data.logs)
               setTotalPages(response.data.totalPages)
            }
         } catch (error) {
            console.error('Failed to load activity logs:', error)
         } finally {
            setLoading(false)
         }
      }
      loadLogs()
   }, [currentPage, debouncedQuery])

   return (
      <Dashboard title="Notification / Activity Log" icon="solar:clock-circle-linear" active="notifications">
         <Content>
            <div className="flex items-center justify-between mb-4">
               <Heading>Notification</Heading>
               <div />
            </div>

            <NavContainer>
               <NavWrapper children="Push Notification" onClick={() => navigate('/notifications')} />
               <NavWrapper children="Activity Log" $active />
            </NavContainer>

            <div className="flex items-center gap-2 mt-6 mb-4">
               <div className="flex items-center gap-2 bg-white border border-[#EDEDEE] rounded-md px-[9px] py-[6px] w-[229px] h-[28px]">
                  <Icon icon='solar:magnifer-linear' width={14} className="text-[#B3B3B6] mb-0.5" />
                  <input
                     value={query}
                     onChange={e => setQuery(e.target.value)}
                     placeholder="Search"
                     className="flex-1 bg-transparent outline-none text-[12px] font-medium tracking-tight placeholder:text-[#B3B3B6]" />
               </div>
            </div>

            {loading ? (
               <div className="flex justify-center py-8">
                  <Icon icon="svg-spinners:ring-resize" width={32} className="text-[#C02236]" />
               </div>
            ) : (
               <>
                  <div>
                     <ListHead style={{ gridTemplateColumns: '220px 1fr 240px 120px' }}>
                        <div className="text-[12px] tracking-tight text-[#9C9C9F]">Date</div>
                        <div className="text-[12px] tracking-tight text-[#9C9C9F]">Description</div>
                        <div className="text-[12px] tracking-tight text-[#9C9C9F]">Admin name</div>
                        <div className="text-[12px] tracking-tight text-center ml-14 text-[#9C9C9F]">Role</div>
                     </ListHead>

                     {logs.length === 0 ? (
                        <div className="text-center py-12 text-[#6D6D72]">
                           No activity logs found.
                        </div>
                     ) : (
                        logs.map((log: ActivityLog) => (
                           <ListRow key={log.id} style={{ gridTemplateColumns: '220px 1fr 225px 120px' }}>
                              <div className="text-xs text-[#9C9C9F]">{formatDate(log.date)}</div>
                              <div className="text-sm text-[#101018] font-medium">{log.description}</div>
                              <div className="text-sm text-[#6D6D72]">{log.adminName}</div>
                              <div className="text-sm text-right">
                                 <span className={`px-3 py-1 rounded-full text-xs font-mono-data ${AdminRoleStyle[log.role.toLowerCase()] || 'bg-[#F5F5F6] text-[#6D6D72]'}`}>
                                    {RoleLabels[log.role.toLowerCase()] || log.role}
                                 </span>
                              </div>
                           </ListRow>
                        ))
                     )}
                  </div>

                  {totalPages > 0 && (
                     <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                  )}
               </>
            )}
         </Content>
      </Dashboard>
   )
}
